import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

const RETURN_PATH_KEY = "ec_return_path";
const PASSWORD_RECOVERY_FLAG = "ec_password_recovery_active";

export default function ProtectedRoute({
  children,
  allowedRoles,
}: ProtectedRouteProps) {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const location = useLocation();
  const isPasswordRecovery = (() => {
    try { return sessionStorage.getItem(PASSWORD_RECOVERY_FLAG) === "true"; } catch { return false; }
  })();

  // Persist the current protected path so we can restore after refresh/login
  useEffect(() => {
    if (user && location.pathname !== "/login") {
      try {
        sessionStorage.setItem(RETURN_PATH_KEY, location.pathname + location.search);
      } catch {
        return;
      }
    }
  }, [user, location.pathname, location.search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
        </div>
      </div>
    );
  }

  if (isPasswordRecovery && location.pathname !== "/reset-password") {
    return <Navigate to="/reset-password" replace />;
  }

  if (!user) {
    // Save the intended path before redirecting
    try {
      sessionStorage.setItem(RETURN_PATH_KEY, location.pathname + location.search);
    } catch {
      // Session storage may be unavailable in private browsing.
    }
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to the user's own portal instead of home
    const portalMap: Record<string, string> = {
      courier: "/courier",
      admin: "/admin",
      support: "/support-portal",
      customer: "/account",
      affiliate: "/affiliate",
      partner: "/partner",
    };
    return <Navigate to={portalMap[user.role] || "/"} replace />;
  }

  return <>{children}</>;
}

export { RETURN_PATH_KEY };
